/**
 * Sales Orders CopilotKit Readable State
 * Exposes sales order data to AI assistant
 */

import { useCopilotReadable } from '@copilotkit/react-core'
import { useSelector } from '@ufc/core'
import {
  selectFilteredSalesOrders,
  selectSalesOrderStats,
  selectSelectedSalesOrder,
} from '../store/sales-orders.selectors'
import { calculateUtilization } from '../presentation/components/sales-orders-section/utils'

export function useSalesOrdersReadable() {
  const salesOrders = useSelector(selectFilteredSalesOrders)
  const stats = useSelector(selectSalesOrderStats)
  const selectedSalesOrder = useSelector(selectSelectedSalesOrder)

  useCopilotReadable({
    description: 'Current sales orders list with filtering applied',
    value: salesOrders.map(c => ({
      id: c.id,
      orderNumber: c.orderNumber,
      name: c.name.en,
      type: c.type,
      status: c.status,
      startDate: c.startDate,
      endDate: c.endDate,
      totalValue: c.totalValue,
      usedValue: c.usedValue,
      utilizationPercent: calculateUtilization(c),
      lineCount: c.lines.length,
    })),
  })

  useCopilotReadable({
    description: 'Sales order statistics summary',
    value: {
      activeSalesOrders: stats.active,
      completedSalesOrders: stats.completed,
      draftSalesOrders: stats.draft,
      totalSalesOrders: stats.total,
      totalSalesOrderValue: stats.totalValue,
    },
  })

  useCopilotReadable({
    description: 'Currently selected sales order details',
    value: selectedSalesOrder ? {
      id: selectedSalesOrder.id,
      orderNumber: selectedSalesOrder.orderNumber,
      name: selectedSalesOrder.name.en,
      type: selectedSalesOrder.type,
      status: selectedSalesOrder.status,
      startDate: selectedSalesOrder.startDate,
      endDate: selectedSalesOrder.endDate,
      totalValue: selectedSalesOrder.totalValue,
      usedValue: selectedSalesOrder.usedValue,
      lines: selectedSalesOrder.lines.map(p => ({
        name: p.product.en,
        allocated: p.allocatedQuantity,
        used: p.usedQuantity,
        unitPrice: p.unitPrice,
      })),
      terms: selectedSalesOrder.terms,
      notes: selectedSalesOrder.notes,
    } : null,
  })
}

// Legacy alias for backwards compatibility
export const useContractsReadable = useSalesOrdersReadable
